import { useEffect, useRef, useState, useCallback } from "react";
import * as Blockly from "blockly";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { Code2, RotateCcw, ArrowRight, Blocks, BookmarkPlus } from "lucide-react";
import { Button } from "../ui/button";
import { defineProgramBlocks, installGenerators, workspaceToPython, buildToolbox, blocksBannedBlocks, getTheme, defaultWorkspaceState } from "./blocksSetup";
import { defineNavBlocks, buildNavToolbox, navBannedBlocks, defaultNavWorkspaceState } from "./navBlocks";
import { registerFilteredCategories } from "./dynamicCategories";
import { useMode } from "../../context/ModeContext";
import { draftKey, loadDraft, saveDraft } from "../../lib/blocksDraft";

const useDarkMode = () => {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(document.documentElement.classList.contains("dark")));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

// "The Python your blocks make" - read-only mirror of the generated code
export const BlocksPythonPanel = ({ code, onContinueInPython, height = "160px", className = "" }) => {
  const dark = useDarkMode();
  return (
    <div className={`rounded-xl border border-border overflow-hidden ${className}`} data-testid="blocks-python-panel">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
          <Code2 className="w-3.5 h-3.5" /> The Python your blocks make
        </span>
        {onContinueInPython && (
          <button
            onClick={() => onContinueInPython(code)}
            className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
            data-testid="blocks-continue-python-btn"
          >
            Continue in Python <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      <CodeMirror value={code} height={height} extensions={[python()]} theme={dark ? oneDark : undefined} editable={false} basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: false }} className="text-xs" />
    </div>
  );
};

// Blockly workspace + live Python panel. Reports generated Python + workspace JSON upward.
// `rules` (Blocks mode) / `navRules` (Junior) carry the question's excluded_categories / excluded_blocks.
export const BlocksWorkspace = ({ questionId, inputs = [], starterWorkspace, initialWorkspace, storageId, height = 420, onChange, onContinueInPython, onSaveAsSolution, hidePythonPanel = false, variant = "program", navRules = null, rules = null, title, headerExtra = null, openToolbox = true }) => {
  const draftOwner = useMode()?.draftOwner;
  const draftId = storageId || questionId;
  const saveKey = draftKey(draftId, draftOwner);
  const isNav = variant === "nav";
  const hatType = isNav ? "nav_start" : "program_start";
  const emptyState = isNav ? defaultNavWorkspaceState : defaultWorkspaceState;
  const hostRef = useRef(null);
  const wsRef = useRef(null);
  const [pyCode, setPythonCode] = useState("");
  const dark = useDarkMode();

  const emit = useCallback(() => {
    const ws = wsRef.current;
    if (!ws) return;
    let code = "";
    try {
      code = workspaceToPython(ws, hatType);
    } catch (e) {
      code = `# Could not generate code: ${e.message}\n`;
    }
    setPythonCode(code);
    const state = Blockly.serialization.workspaces.save(ws);
    saveDraft(draftId, draftOwner, state);
    onChange?.(code, state);
  }, [onChange, draftId, draftOwner, hatType]);

  useEffect(() => {
    if (!hostRef.current) return;
    defineProgramBlocks(inputs);
    installGenerators(inputs);
    defineNavBlocks();
    const ws = Blockly.inject(hostRef.current, {
      toolbox: isNav ? buildNavToolbox(navRules || {}) : buildToolbox(inputs.length > 0, rules || {}),
      renderer: "zelos",
      theme: getTheme(dark) || undefined,
      trashcan: true,
      sounds: false,
      zoom: { controls: true, wheel: false, startScale: 0.72, maxScale: 1.4, minScale: 0.4, scaleSpeed: 1.15 },
      move: { scrollbars: { horizontal: true, vertical: true }, drag: true, wheel: true },
      grid: { spacing: 24, length: 2, colour: dark ? "#1f2a44" : "#e2e8f0", snap: true },
    });
    registerFilteredCategories(ws, isNav ? navBannedBlocks(navRules || {}) : blocksBannedBlocks(rules || {}));
    wsRef.current = ws;
    // Automation hook (QA scripts): window.__cdBlocks.load(workspaceJson)
    window.__cdBlocks = { Blockly, workspace: ws, load: (state) => Blockly.serialization.workspaces.load(state, ws) };

    let initial = loadDraft(draftId, draftOwner);
    if (!initial) initial = initialWorkspace || starterWorkspace || emptyState();
    try {
      Blockly.serialization.workspaces.load(initial, ws);
    } catch {
      Blockly.serialization.workspaces.load(emptyState(), ws);
    }
    if (ws.getBlocksByType(hatType, false).length === 0) {
      Blockly.serialization.blocks.append(emptyState().blocks.blocks[0], ws);
    }
    ws.addChangeListener((e) => {
      if (e.isUiEvent || e.type === Blockly.Events.FINISHED_LOADING) return;
      emit();
    });
    // Keep the block menu open while dragging blocks out (Blockly closes it by default). Phones keep the
    // auto-close so the menu doesn't cover the whole canvas. With autoClose off, Blockly's metrics place
    // the canvas to the right of the open menu, so blocks never sit underneath it.
    const flyout = ws.getFlyout();
    if (flyout && window.innerWidth >= 768) flyout.autoClose = false;
    emit();
    // Start with the first block category open so students see the blocks straight away.
    // Deferred: Blockly's focus manager blurs the toolbox (closing the flyout) while the workspace finishes initialising.
    let openTimer = null;
    if (openToolbox) {
      openTimer = setTimeout(() => {
        try { ws.getToolbox()?.selectItemByPosition(0); } catch (err) { console.warn("toolbox open failed", err); }
      }, 0);
    }
    const onResize = () => Blockly.svgResize(ws);
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(openTimer);
      window.removeEventListener("resize", onResize);
      ws.dispose();
      wsRef.current = null;
      if (window.__cdBlocks?.workspace === ws) delete window.__cdBlocks;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, saveKey, JSON.stringify(inputs), variant, JSON.stringify(navRules), JSON.stringify([rules?.excluded_categories, rules?.excluded_blocks])]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    const theme = getTheme(dark);
    if (theme) ws.setTheme(theme);
  }, [dark]);

  const reset = () => {
    const ws = wsRef.current;
    if (!ws) return;
    ws.clear();
    Blockly.serialization.workspaces.load(starterWorkspace || emptyState(), ws);
    emit();
  };

  return (
    <div className="space-y-3" data-testid="blocks-workspace">
      <div className="md:hidden rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-xs px-3 py-2" data-testid="blocks-small-screen-notice">
        Blocks work best on a tablet or computer. On a phone, try landscape.
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Blocks className="w-4 h-4" /> {title || (isNav ? "Program your drill with blocks" : "Build your program with blocks")}
        </span>
        <div className="flex items-center gap-1">
          {headerExtra}
          {onSaveAsSolution && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 h-8 text-sky-600 dark:text-sky-400"
              title="Admin: store these blocks as the question's sample solution (verified against the test cases)"
              onClick={() => wsRef.current && onSaveAsSolution(Blockly.serialization.workspaces.save(wsRef.current), pyCode)}
              data-testid="blocks-save-solution-btn"
            >
              <BookmarkPlus className="w-3.5 h-3.5" /> Save as sample solution
            </Button>
          )}
          <Button variant="ghost" size="sm" className="gap-1.5 h-8" onClick={reset} data-testid="blocks-reset-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Start over
          </Button>
        </div>
      </div>
      <div
        ref={hostRef}
        className="rounded-xl overflow-hidden border border-border"
        style={{ height, width: "100%" }}
        data-testid="blocks-canvas"
      />
      {!hidePythonPanel && <BlocksPythonPanel code={pyCode} onContinueInPython={onContinueInPython} />}
    </div>
  );
};

